import { useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth.js';
import { login, register, ApiError } from '../api/client.js';

export function LoginPage() {
  const { setToken } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [tenantType, setTenantType] = useState<'manufacturer' | 'reseller'>('manufacturer');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isRegister) {
        const data = await register({ email, password, name, organizationName, tenantType });
        setToken(data.token);
      } else {
        const data = await login({ email, password });
        setToken(data.token);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClasses =
    'w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2.5 text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-950 px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <img src="/ologo/ologo-100.png" alt="" className="h-12" />
            <span className="text-3xl font-bold">
              <span className="text-white">Open</span>
              <span className="text-brand-400">Quote Pro</span>
            </span>
          </div>
          <p className="mt-2 text-gray-400">Professional Quoting Platform</p>
        </div>

        <div className="rounded-lg border border-gray-800 bg-gray-900 p-8">
          <h2 className="mb-6 text-xl font-semibold text-gray-100">
            {isRegister ? 'Create Account' : 'Sign In'}
          </h2>

          {error && (
            <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <>
                <div>
                  <label htmlFor="name" className="mb-1 block text-sm text-gray-400">
                    Your Name
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={inputClasses}
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Account Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setTenantType('manufacturer')}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        tenantType === 'manufacturer'
                          ? 'border-brand-500 bg-brand-500/10'
                          : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                      }`}
                    >
                      <div className="font-medium text-white text-sm">Manufacturer</div>
                      <div className="text-xs text-gray-400 mt-1">
                        Publish catalogs, create quotes for resellers
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setTenantType('reseller')}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        tenantType === 'reseller'
                          ? 'border-brand-500 bg-brand-500/10'
                          : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                      }`}
                    >
                      <div className="font-medium text-white text-sm">Reseller</div>
                      <div className="text-xs text-gray-400 mt-1">
                        Subscribe to catalogs, build quotes for customers
                      </div>
                    </button>
                  </div>
                </div>
                <div>
                  <label htmlFor="org" className="mb-1 block text-sm text-gray-400">
                    Organization Name
                  </label>
                  <input
                    id="org"
                    type="text"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    className={inputClasses}
                    placeholder="Acme Corp"
                    required
                  />
                </div>
              </>
            )}

            <div>
              <label htmlFor="email" className="mb-1 block text-sm text-gray-400">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClasses}
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="mb-1 block text-sm text-gray-400">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClasses}
                placeholder={isRegister ? 'Minimum 8 characters' : 'Enter your password'}
                required
                minLength={isRegister ? 8 : undefined}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-brand-600 px-4 py-2.5 font-medium text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
            >
              {loading
                ? isRegister
                  ? 'Creating Account...'
                  : 'Signing In...'
                : isRegister
                  ? 'Create Account'
                  : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-400">
            {isRegister ? (
              <>
                Already have an account?{' '}
                <button
                  onClick={() => {
                    setIsRegister(false);
                    setError('');
                  }}
                  className="text-brand-400 hover:text-brand-300"
                >
                  Sign in
                </button>
              </>
            ) : (
              <>
                Don&apos;t have an account?{' '}
                <button
                  onClick={() => {
                    setIsRegister(true);
                    setError('');
                  }}
                  className="text-brand-400 hover:text-brand-300"
                >
                  Create one
                </button>
              </>
            )}
          </div>
        </div>

        <div className="mt-6 text-center text-xs text-gray-500">
          By using OpenQuote Pro, you agree to our{' '}
          <a href="/terms" className="text-brand-400 hover:underline">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy" className="text-brand-400 hover:underline">
            Privacy Policy
          </a>
          .
        </div>
      </div>
    </div>
  );
}
